import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, fileUrl } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { WinterFindings } from "@/components/WinterFindings";
import {
  ClipboardCheck, Play, Square, Camera, AlertTriangle, Check, X, MinusCircle,
  CircleDot, Clock, MapPin, Plus, Loader2, ImagePlus,
  Bath, ShowerHead, Sofa, BedDouble, UtensilsCrossed, Shirt, Trash2, SprayCan,
  Waves, TreePalm, Sparkles, Wind, Droplets, Wrench,
} from "lucide-react";

function pickItemIcon(label = "") {
  const s = label.toLowerCase();
  if (s.includes("μπάνι") || s.includes("τουαλ")) return Bath;
  if (s.includes("ντους") || s.includes("ντουζ")) return ShowerHead;
  if (s.includes("σαλόν")) return Sofa;
  if (s.includes("υπνο") || s.includes("κρεβάτ") || s.includes("λευκ")) return BedDouble;
  if (s.includes("κουζίν")) return UtensilsCrossed;
  if (s.includes("πετσέτ")) return Shirt;
  if (s.includes("σκουπίδ") || s.includes("απορρίμ")) return Trash2;
  if (s.includes("amenities") || s.includes("αναπλήρ")) return SprayCan;
  if (s.includes("πισίν") || s.includes("pool")) return Waves;
  if (s.includes("βεράντ") || s.includes("μπαλκόν") || s.includes("κήπ") || s.includes("εξωτερ")) return TreePalm;
  if (s.includes("δάπεδ") || s.includes("πάτωμ")) return Sparkles;
  if (s.includes("κλιματ")) return Wind;
  if (s.includes("νερό") || s.includes("υδρ")) return Droplets;
  if (s.includes("συντήρ") || s.includes("επισκευ")) return Wrench;
  return ClipboardCheck;
}

const STATE_CFG = {
  pending: { label: "Εκκρεμεί", icon: CircleDot, cls: "text-[#B9B0A2]" },
  done: { label: "Έγινε", icon: Check, cls: "text-[#3F6B4C]" },
  skip: { label: "Παράλειψη", icon: MinusCircle, cls: "text-[#9A6A3A]" },
  issue: { label: "Βλάβη", icon: AlertTriangle, cls: "text-[#A1493A]" },
};

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.file_id;
}

export default function FieldOps({ scope = null }) {
  const qc = useQueryClient();
  const [cat, setCat] = useState(scope === "maintenance" ? "maintenance" : "housekeeping");
  const [cleanType, setCleanType] = useState("freshen_up");
  const [unitId, setUnitId] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [issueOpen, setIssueOpen] = useState(false);

  const { data: boot } = useQuery({ queryKey: ["fo-boot"], queryFn: async () => (await api.get("/fieldops/bootstrap")).data });
  const { data: recent = [] } = useQuery({
    queryKey: ["fo-visits-mine"],
    queryFn: async () => (await api.get("/fieldops/visits?mine=true")).data,
  });
  const { data: active } = useQuery({
    queryKey: ["fo-visit", activeId],
    queryFn: async () => (await api.get(`/fieldops/visits/${activeId}`)).data,
    enabled: !!activeId,
  });

  useEffect(() => {
    if (!activeId) {
      const ip = recent.find((v) => v.status === "in_progress");
      if (ip) setActiveId(ip.id);
    }
  }, [recent, activeId]);

  const units = boot?.units || [];
  const allCats = boot?.categories || [];
  const allowedKeys = scope === "housekeeping" ? ["housekeeping"] : scope === "maintenance" ? ["maintenance", "gardening"] : allCats.map((c) => c.key);
  const catList = allCats.filter((c) => allowedKeys.includes(c.key));
  const scopeTitle = scope === "housekeeping" ? "Housekeeping" : scope === "maintenance" ? "Συντήρηση" : "Οι Εργασίες μου";
  useEffect(() => { if (allowedKeys.length && !allowedKeys.includes(cat)) setCat(allowedKeys[0]); /* eslint-disable-next-line */ }, [boot, scope]);
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["fo-visits-mine"] }); qc.invalidateQueries({ queryKey: ["fo-visit", activeId] }); };

  const start = useMutation({
    mutationFn: async () => (await api.post("/fieldops/visits", { unit_id: unitId, category: cat, clean_type: cat === "housekeeping" ? cleanType : "" })).data,
    onSuccess: (v) => { setActiveId(v.id); invalidate(); toast.success("Ξεκίνησε η εργασία"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const saveVisit = useMutation({
    mutationFn: async (payload) => (await api.put(`/fieldops/visits/${activeId}`, payload)).data,
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(apiErr(e)),
  });
  const submit = useMutation({
    mutationFn: async () => (await api.post(`/fieldops/visits/${activeId}/submit`, {
      checklist: active.checklist, notes: active.notes || "", photos: active.photos || [], findings: active.findings || [],
    })).data,
    onSuccess: () => { toast.success("Υποβλήθηκε — ειδοποιήθηκαν οι υπεύθυνοι"); closeActive("submitted"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const cancelVisit = useMutation({
    mutationFn: async () => (await api.post(`/fieldops/visits/${activeId}/cancel`)).data,
    onSuccess: () => { toast.success("Η εργασία ακυρώθηκε"); closeActive("cancelled"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  function closeActive(status) {
    qc.setQueryData(["fo-visits-mine"], (old) => (Array.isArray(old) ? old.map((v) => (v.id === activeId ? { ...v, status } : v)) : old));
    setActiveId(null);
    invalidate();
  }

  const setItem = (idx, patch) => {
    const checklist = active.checklist.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    qc.setQueryData(["fo-visit", activeId], { ...active, checklist });
    saveVisit.mutate({ checklist, notes: active.notes || "", photos: active.photos || [] });
  };
  const addPhoto = async (file, kind) => {
    try {
      const file_id = await uploadFile(file);
      const photos = [...(active.photos || []), { file_id, kind, caption: "" }];
      qc.setQueryData(["fo-visit", activeId], { ...active, photos });
      saveVisit.mutate({ checklist: active.checklist, notes: active.notes || "", photos });
      toast.success("Φωτογραφία προστέθηκε");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setFindings = (findings, persist = false) => {
    qc.setQueryData(["fo-visit", activeId], { ...active, findings });
    if (persist) saveVisit.mutate({ checklist: active.checklist, notes: active.notes || "", photos: active.photos || [], findings });
  };
  const uploadSafe = async (file) => {
    try { return await uploadFile(file); } catch (e) { toast.error(apiErr(e)); return null; }
  };
  const isWinter = active?.clean_type === "winter_maintenance";

  const doneCount = active ? active.checklist.filter((c) => c.state === "done").length : 0;

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="field-ops-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Εργασίες Πεδίου</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2">
          <ClipboardCheck className="h-7 w-7" /> {scopeTitle}
        </h1>
      </div>

      {!active && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-5" data-testid="start-visit-card">
          <h2 className="font-serif text-xl text-[#45423C]">Νέα εργασία</h2>
          {catList.length > 1 && (
            <Field label="Κατηγορία">
              <div className="flex flex-wrap gap-2">
                {catList.map((c) => (
                  <button key={c.key} data-testid={`cat-btn-${c.key}`} onClick={() => setCat(c.key)}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold border ${cat === c.key ? "bg-[#6E7A63] text-white border-[#6E7A63]" : "border-[#E3DED4] text-[#6F6A60] bg-white hover:border-[#B9AE9A]"}`}>{c.label}</button>
                ))}
              </div>
            </Field>
          )}
          <Field label="Μονάδα">
            <div className="space-y-3">
              {[["villa", "Βίλες"], ["common", "Κοινόχρηστοι χώροι"]].map(([tp, lbl]) => (
                <div key={tp}>
                  <div className="text-[11px] uppercase tracking-wider text-[#8A8577] mb-1.5">{lbl}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {units.filter((u) => (tp === "villa" ? u.type === "villa" : u.type !== "villa")).map((u) => (
                      <button key={u.id} data-testid={`unit-btn-${u.id}`} onClick={() => setUnitId(u.id)}
                        className={`px-3 py-3 rounded-xl text-sm font-medium border text-center transition-colors ${unitId === u.id ? "bg-[#45423C] text-white border-[#45423C]" : "border-[#E3DED4] text-[#45423C] bg-white hover:border-[#B9AE9A]"}`}>{u.name}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Field>
          {cat === "housekeeping" && (
            <Field label="Τύπος καθαρισμού">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {(boot?.clean_types || []).map((c) => (
                  <button key={c.key} data-testid={`clean-${c.key}`} onClick={() => setCleanType(c.key)}
                    className={`px-4 py-3 rounded-xl text-sm border text-center ${cleanType === c.key ? "bg-[#45423C] text-white border-[#45423C]" : "border-[#E3DED4] text-[#6F6A60] bg-white hover:border-[#B9AE9A]"}`}>{c.label}</button>
                ))}
              </div>
            </Field>
          )}
          <button data-testid="start-visit-btn" disabled={!unitId || start.isPending} onClick={() => start.mutate()}
            className="rounded-xl bg-[#6E7A63] text-white px-5 py-3 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            <Play className="h-4 w-4" /> Έναρξη εργασίας
          </button>
        </div>
      )}

      {active && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-4" data-testid="active-visit-card">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-serif text-xl text-[#45423C] flex items-center gap-2"><MapPin className="h-5 w-5" /> {active.unit_name}</h2>
              <p className="text-sm text-[#6F6A60]">{(boot?.categories || []).find((c) => c.key === active.category)?.label}{active.clean_type ? ` · ${(boot?.clean_types || []).find((c) => c.key === active.clean_type)?.label}` : ""}</p>
            </div>
            <span className="text-xs inline-flex items-center gap-1 text-[#9A6A3A] bg-[#F3EADF] px-2.5 py-1 rounded-full"><Clock className="h-3 w-3" /> Σε εξέλιξη</span>
          </div>

          {isWinter ? (
            <WinterFindings areas={boot?.villa_areas?.[active.unit_name] || boot?.winter_areas || []} findings={active.findings} onChange={setFindings} upload={uploadSafe} />
          ) : (<>
          <div className="text-sm text-[#45423C] font-medium">Checklist · {doneCount}/{active.checklist.length}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {active.checklist.map((it, idx) => {
              const TileIcon = pickItemIcon(it.label);
              const tileCls = it.state === "done" ? "border-[#3F6B4C]/40 bg-[#EEF3EE]"
                : it.state === "skip" ? "border-[#9A6A3A]/40 bg-[#F6EEE2]"
                : it.state === "issue" ? "border-[#A1493A]/40 bg-[#FBF1ED]"
                : "border-[#E3DED4] bg-white";
              return (
                <div key={it.item_id} data-testid={`check-item-${idx}`} className={`rounded-2xl border p-3 flex flex-col items-center text-center gap-2 transition-colors ${tileCls}`}>
                  <TileIcon className="h-7 w-7 text-[#6E7A63]" />
                  <span className={`text-sm font-medium leading-tight ${it.state === "done" ? "text-[#3F6B4C]" : "text-[#45423C]"}`}>
                    {it.label}{it.required && <span className="text-[#C86235] ml-0.5">*</span>}
                  </span>
                  <div className="flex items-center gap-1.5 mt-auto pt-1">
                    {["done", "skip", "issue"].map((st) => {
                      const C = STATE_CFG[st];
                      return (
                        <button key={st} data-testid={`item-${idx}-${st}`} onClick={() => setItem(idx, { state: it.state === st ? "pending" : st })}
                          title={C.label} className={`h-9 w-9 rounded-full flex items-center justify-center border ${it.state === st ? "border-current bg-white " + C.cls : "border-[#E3DED4] text-[#C4BCAD] bg-white/60"}`}>
                          <C.icon className="h-4 w-4" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          </>)}

          <div className="grid sm:grid-cols-2 gap-3">
            <PhotoUpload testid="photo-before" label="Φωτό πριν" onPick={(f) => addPhoto(f, "before")} />
            <PhotoUpload testid="photo-after" label="Φωτό μετά / ολοκλήρωση" onPick={(f) => addPhoto(f, "after")} />
          </div>
          {(active.photos || []).length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="visit-photos">
              {active.photos.map((p, i) => (
                <div key={p.file_id || i} className="relative">
                  <img src={fileUrl(p.file_id)} alt={p.kind} className="h-20 w-20 object-cover rounded-lg border border-[#E3DED4]" />
                  <span className="absolute bottom-0 left-0 text-[9px] bg-black/60 text-white px-1 rounded-tr">{p.kind === "before" ? "πριν" : "μετά"}</span>
                </div>
              ))}
            </div>
          )}

          <Field label="Σημειώσεις">
            <textarea data-testid="visit-notes" value={active.notes || ""} rows={2}
              onChange={(e) => qc.setQueryData(["fo-visit", activeId], { ...active, notes: e.target.value })}
              onBlur={(e) => saveVisit.mutate({ checklist: active.checklist, notes: e.target.value, photos: active.photos || [] })}
              className="w-full rounded-xl border border-[#E3DED4] px-3 py-2 text-sm" />
          </Field>

          {active.issues?.length > 0 && (
            <div className="rounded-xl bg-[#FBF1ED] border border-[#E7CEC6] p-3 text-sm text-[#A1493A]">
              {active.issues.length} αναφορές βλάβης σε αυτή την εργασία
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <button data-testid="report-issue-btn" onClick={() => setIssueOpen(true)} className="rounded-xl border border-[#E7CEC6] text-[#A1493A] px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> Αναφορά βλάβης</button>
            <button data-testid="cancel-visit-btn" onClick={() => { if (window.confirm("Ακύρωση αυτής της εργασίας; Δεν θα αποθηκευτεί ως ολοκληρωμένη.")) cancelVisit.mutate(); }} disabled={cancelVisit.isPending} className="rounded-xl border border-[#E3DED4] text-[#6F6A60] px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"><X className="h-4 w-4" /> Ακύρωση εργασίας</button>
            <button data-testid="submit-visit-btn" onClick={() => { if (active.checklist.some((c) => c.required && c.state === "pending") && !window.confirm("Υπάρχουν υποχρεωτικά σημεία που εκκρεμούν. Υποβολή;")) return; submit.mutate(); }} disabled={submit.isPending} className="ml-auto rounded-xl bg-[#45423C] text-white px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"><Square className="h-4 w-4" /> Ολοκλήρωση & υποβολή</button>
          </div>
        </div>
      )}

      <div>
        <h2 className="font-serif text-xl text-[#45423C] mb-3">Πρόσφατες εργασίες μου</h2>
        <div className="space-y-2" data-testid="recent-visits">
          {recent.filter((v) => allowedKeys.includes(v.category)).slice(0, 15).map((v) => (
            <button key={v.id} data-testid={`recent-${v.id}`} onClick={() => v.status === "in_progress" && setActiveId(v.id)}
              className="w-full text-left rounded-xl bg-white border border-[#E3DED4] px-4 py-3 flex items-center justify-between hover:border-[#B9AE9A]">
              <div>
                <div className="text-sm font-medium text-[#45423C]">{v.unit_name} · {(boot?.categories || []).find((c) => c.key === v.category)?.label}</div>
                <div className="text-xs text-[#8A8577]">{(v.started_at || "").slice(0, 16).replace("T", " ")} · {v.duration_min || 0}′</div>
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${v.status === "submitted" ? "bg-[#E2ECE4] text-[#3F6B4C]" : v.status === "cancelled" ? "bg-[#F1EDE6] text-[#8A8577]" : "bg-[#F3EADF] text-[#9A6A3A]"}`}>{v.status === "submitted" ? "Υποβλήθηκε" : v.status === "cancelled" ? "Ακυρώθηκε" : "Σε εξέλιξη"}</span>
            </button>
          ))}
          {recent.length === 0 && <p className="text-sm text-[#6F6A60]">Καμία εργασία ακόμη.</p>}
        </div>
      </div>

      {issueOpen && active && (
        <IssueModal visitId={activeId} unitId={active.unit_id} onClose={() => setIssueOpen(false)} onDone={() => { setIssueOpen(false); invalidate(); }} />
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold block mb-1">{label}</label>
    {children}
  </div>
);

function PhotoUpload({ label, onPick, testid, multiple = false }) {
  const [busy, setBusy] = useState(false);
  return (
    <label data-testid={testid} className="cursor-pointer rounded-xl border border-dashed border-[#D8D0C2] px-4 py-3 text-sm text-[#6F6A60] flex items-center justify-center gap-2 hover:border-[#6E7A63]">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} {label}
      <input type="file" accept="image/*" multiple={multiple} className="hidden" onChange={async (e) => {
        const files = Array.from(e.target.files || []); if (!files.length) return; setBusy(true);
        for (const f of files) { await onPick(f); }
        setBusy(false); e.target.value = "";
      }} />
    </label>
  );
}

const AREAS = [["bedroom", "Υπνοδωμάτιο"], ["bathroom", "Μπάνιο"], ["kitchen", "Κουζίνα"], ["living", "Σαλόνι"],
  ["outdoor_pool", "Εξωτερική πισίνα"], ["terrace", "Βεράντα / Μπαλκόνι"], ["garden", "Κήπος"], ["entrance", "Είσοδος"], ["other", "Άλλο"]];

function IssueModal({ visitId, unitId, onClose, onDone }) {
  const [form, setForm] = useState({ category: "other", severity: "medium", area: "", description: "", photos: [] });
  const [busy, setBusy] = useState(false);
  const save = useMutation({
    mutationFn: async () => (await api.post(`/fieldops/visits/${visitId}/issue`, { ...form, unit_id: unitId })).data,
    onSuccess: () => { toast.success("Η βλάβη αναφέρθηκε — ειδοποιήθηκαν οι υπεύθυνοι"); onDone(); },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 overflow-y-auto" data-testid="issue-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 my-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-[#45423C]">Αναφορά βλάβης</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-[#6F6A60]" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Κατηγορία">
              <select data-testid="issue-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full rounded-xl border border-[#E3DED4] px-3 py-2 bg-white">
                <option value="plumbing">Υδραυλικά</option><option value="electrical">Ηλεκτρικά</option><option value="ac">Κλιματισμός</option><option value="pool">Πισίνα</option><option value="appliance">Συσκευή</option><option value="other">Άλλο</option>
              </select>
            </Field>
            <Field label="Σοβαρότητα">
              <select data-testid="issue-severity" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="w-full rounded-xl border border-[#E3DED4] px-3 py-2 bg-white">
                <option value="low">Χαμηλή</option><option value="medium">Μεσαία</option><option value="high">Υψηλή</option>
              </select>
            </Field>
          </div>
          <Field label="Χώρος">
            <select data-testid="issue-area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="w-full rounded-xl border border-[#E3DED4] px-3 py-2 bg-white">
              <option value="">Επιλέξτε…</option>
              {AREAS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label="Περιγραφή"><textarea data-testid="issue-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-xl border border-[#E3DED4] px-3 py-2" /></Field>
          <PhotoUpload testid="issue-photo" multiple label="Φωτογραφίες βλάβης (μία ή περισσότερες)" onPick={async (f) => { setBusy(true); try { const id = await uploadFile(f); setForm((p) => ({ ...p, photos: [...p.photos, id] })); } catch (e) { toast.error(apiErr(e)); } setBusy(false); }} />
          {form.photos.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="issue-photos">
              {form.photos.map((id) => (
                <div key={id} className="relative">
                  <img src={fileUrl(id)} alt="" className="h-16 w-16 object-cover rounded-lg border border-[#E3DED4]" />
                  <button type="button" data-testid={`issue-photo-remove-${id}`} onClick={() => setForm((p) => ({ ...p, photos: p.photos.filter((x) => x !== id) }))}
                    className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-white border border-[#E3DED4] flex items-center justify-center"><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-6 flex gap-3">
          <button data-testid="issue-save" disabled={!form.area || busy || save.isPending} onClick={() => save.mutate()} className="flex-1 rounded-xl bg-[#A1493A] text-white py-2.5 text-sm font-semibold disabled:opacity-50">Αποστολή</button>
          <button onClick={onClose} className="rounded-xl border border-[#E3DED4] px-4 py-2.5 text-sm">Άκυρο</button>
        </div>
      </div>
    </div>
  );
}
